/* eslint-disable complexity */
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import styles from './index.less';
import i18n from '@i18next/i18n';
import { tencentDocOpenUrlReload } from '@tencent/tencent_doc_open_url';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ILogger, SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { EditEvents } from '@src/slide/domain/slide-app/core/interfaces/event.interface';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import SnackbarPc from '@tencent/dui/lib/components/Snackbar';
import SnackbarM from '@tencent/dui-mobile/lib/components/Snackbar';
import type show from '@tencent/dui/lib/components/Snackbar/show';
import type { ISnackbarAction, TSnackbarShowConfig } from '@tencent/dui/lib/components/Snackbar/interface';
import type { IContextProps } from '@tencent/dui/lib/utils/show';
import { IPcEditorViewableServiceLayer } from '@src/slide/application/entry/toc/editor/pc/service-layer/viewable-service-layer.interface';
import ConnectUsModal, { IConnectUsModalShowParams, IHowToDetailConfig } from '../connect-us-modal';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';

export interface ICommonTipsConfig {
    id?: string;
    block?: boolean;
    message?: string;
    commonTipsType?: string;
    errorCode?: number;
    closable?: boolean;
    actionType?: number;
    actionText?: string;
    autoClose?: boolean;
    duration?: number;
    customerActions?: ISnackbarAction[];
    shouldCloseAfterAction?: boolean;
    onlyShowOne?: boolean
    errCode?: number;
    callBack?: (...params: any) => any;
    closeCallback?: (...params: any) => any;
    howToDetailConfig?: IHowToDetailConfig;
    style?: object;
}

export enum CommonTipsActionType {
    NONE = 1 << 1,
    REFRESH = 1 << 2, // 刷新
    CONNECTUS = 1 << 3, // 联系我们
    HOWTODEAL = 1 << 4, // 如何处理
    LOGIN = 1 << 5, // 登录
    EXPORT = 1 << 6, // 导出
    EXPORT_SNAP_SHOT = 1 << 7, // 导出当前内存快照
    CREATE_COPY_DOC = 1 << 8, // 创建副本
    REPORT_WEBLOG = 1 << 9, // 上报weblog
}

export const CommonTipsType = {
    INFO: 'info', // 提示
    SUCCESS: 'success', // 成功
    WARNING: 'warning', // 警告
    ERROR: 'error',  // 错误
    LOADING: 'loading',  // 加载中
};

const moduleName = '[CT]';
const snackBar = (browser.isMobile ? SnackbarM : SnackbarPc) as unknown as typeof SnackbarPc;

export default class CommonTips {
    public static instance: CommonTips;
    public static getInstance() {
        if (!CommonTips.instance) {
            CommonTips.instance = new CommonTips();
        }
        return CommonTips.instance;
    }

    private snackBarInstance: ReturnType<typeof show> | null;
    private logger: ILogger | undefined;

    private currentCommonTipsConfig: ICommonTipsConfig | null = null;
    private storageData: ICommonTipsConfig | null = null;
    private alreadyShowOne = false; // 当前是否已展示唯一不可替代的tip

    public constructor() {
        SlideDataServices.Infra.Logger.promise().then(logger => this.logger = logger);
    }


    public show(commonTipsConfig: ICommonTipsConfig) {
        const {
            errorCode = commonTipsConfig.message,
            duration = 2000,
            block = true,
            closeCallback,
            onlyShowOne = false,
            autoClose,
        } = commonTipsConfig;
        if (this.alreadyShowOne) {
            return;
        }
        this.clearStorage();
        this.hide();
        this.alreadyShowOne = onlyShowOne;
        this.currentCommonTipsConfig = commonTipsConfig;
        const actions = this.getActions(commonTipsConfig);
        const snackBarConfig = this.getSnackBarConfig(commonTipsConfig);
        if (actions.length) snackBarConfig.action = actions;
        if (block) this.showMask();

        this.logger?.warn(moduleName, `Show errorCode:${errorCode}`);
        this.snackBarInstance = snackBar.show(snackBarConfig);
        if (autoClose && duration) {
            this.currentCommonTipsConfig = null;
            setTimeout(() => {
                this.hideMask();
                closeCallback?.();
            }, duration);
        }
    }

    public pause() {
        if (this.currentCommonTipsConfig) {
            this.storageData = this.currentCommonTipsConfig;
            this.hide();
        }
    }

    public resume() {
        if (this.storageData) {
            this.show(this.storageData);
        }
    }

    public storage(param: ICommonTipsConfig) {
        this.storageData = param;
    }

    public clearStorage() {
        this.storageData = null;
    }

    public hide() {
        this.currentCommonTipsConfig = null;
        this.alreadyShowOne = false;
        this.hideMask();
        if (this.snackBarInstance) {
            this.snackBarInstance.close();
            this.snackBarInstance = null;
        }
    }

    /**
     * 通过 id 关闭 snackbar
     * @param id
     */
    public hideById(id: string, force = browser.isMobile) {
        // 移动端DUI版本太低，不支持关闭指定id的snackbar
        if (force) {
            const isExist = this.currentCommonTipsConfig?.id === id;
            if (isExist) {
                this.hide();
            }
            return;
        }
        const isExist = this.duiSnackBars?.find(snackBar => snackBar.instance.props.id === id);
        if (!isExist) return;
        this.currentCommonTipsConfig = null;
        this.alreadyShowOne = false;
        this.hideMask();
        snackBar.closeById?.(id);
        this.snackBarInstance = null;
    }

    public showMask() {
        const maskDom = document.createElement('div');
        maskDom.className = styles['toast-mask'];
        maskDom.id = 'common-tips';
        document.body.appendChild(maskDom);
    }

    public hideMask() {
        const maskDom = document.getElementById('common-tips');
        maskDom?.parentNode?.removeChild(maskDom);
    }

    public getById(id: string) {
        return this.duiSnackBars?.find(snackBar => snackBar?.instance?.props?.id === id);
    }

    private getExtensionCallBackAdapter() {
        return this.appService?.viewableServiceLayer.extensionService?.getCallBackAdapter();
    }

    private getMenuManager() {
        return this.appService.editableServiceLayer?.menuManager;
    }

    private get appService() {
        return AppServiceProvider.getInstance().getAppServices() as (IMobileEditorAppServices | IPcEditorAppServices);
    }

    private get duiSnackBars() {
        // @ts-ignore TS2339
        // eslint-disable-next-line no-underscore-dangle
        return document.body.__dui_visible_snackbars__ as Array<{
            instance: (typeof snackBar & { props: TSnackbarShowConfig })
        }>;
    }

    private getSnackBarConfig = (param: ICommonTipsConfig) => {
        const {
            message,
            commonTipsType = CommonTipsType.INFO,
            closable = false,
            autoClose = false,
            duration = 2000,
            closeCallback,

            /** 点击 action 后是否关闭 snackbar, 默认 true; false 时 closable 也必须为 false */
            shouldCloseAfterAction = true,
            id = '',
            style = {},
        } = param;

        const snackBarConfig: TSnackbarShowConfig & IContextProps = {
            message,
            autoClose,
            closable,
            type: commonTipsType as any,
            duration,
            id,
            style,
        };

        // 无法区分 onClose 的触发是来自 关闭按钮/动作按钮 中的哪一个
        // 暂时只能在 shouldCloseAfterAction = false 时禁用 closable
        if (!shouldCloseAfterAction) snackBarConfig.closable = false;
        // 点击 关闭按钮/动作按钮 时会触发这个回调
        // 自动关闭或通过方法关闭不会触发
        snackBarConfig.onClose = () => {
            // 点击 action 不关闭 snackbar
            if (!shouldCloseAfterAction) return false;
            this.hide();
            closeCallback?.();
        };

        return snackBarConfig;
    };

    private getActions = (param: ICommonTipsConfig) => {
        const {
            actionType = CommonTipsActionType.NONE, errorCode = -1, callBack, customerActions = [], actionText,
            howToDetailConfig,
        } = param;
        const actions: ISnackbarAction[] = [];
        if (CommonTipsActionType.REFRESH & actionType) {
            const action = {
                text: actionText ?? i18n.t('刷新'),
                onClick: () => {
                    this.logger?.info(moduleName, 'click refresh');
                    CommonTips.getInstance().hide();
                    tencentDocOpenUrlReload();
                    if (callBack) {
                        callBack(CommonTipsActionType.REFRESH, errorCode);
                    }
                },
            };
            actions.push(action);
        }

        if (CommonTipsActionType.CONNECTUS & actionType) {
            const action = {
                text: actionText ?? (browser.isMobile ? i18n.t('反馈') : i18n.t('联系我们')),
                onClick: () => {
                    this.logger?.info(moduleName, 'click connect us');
                    this.connectUs();
                    if (callBack) {
                        callBack(CommonTipsActionType.CONNECTUS, errorCode);
                    }
                    return false;
                },
            };
            actions.push(action);
        }

        if (CommonTipsActionType.HOWTODEAL & actionType) {
            const action = {
                text: actionText ?? i18n.t('如何处理'),
                onClick: () => {
                    this.logger?.warn(i18n.t('点击 如何处理:{{errorCode}}', { errorCode }));
                    this.userReport();
                    CommonTips.getInstance().hide();
                    const connectUsConfig: IConnectUsModalShowParams = {
                        config: howToDetailConfig,
                        refresh: () => {
                            callBack?.(CommonTipsActionType.REFRESH);
                            tencentDocOpenUrlReload();
                        },
                        createCopyDoc: () => callBack?.(CommonTipsActionType.CREATE_COPY_DOC, this.createCopyDoc),
                        exportSnapshot: () => {
                            this.exportSnapshot();
                            callBack?.(CommonTipsActionType.EXPORT_SNAP_SHOT);
                        },
                        openHistoryList: () => {
                            callBack?.();
                            browser.isPc && this.openHistoryList();
                        },
                        connectUs: () => {
                            this.connectUs();
                            callBack?.(CommonTipsActionType.CONNECTUS);
                        },
                        exportDBToJson: this.exportDBToJson,
                    };
                    ConnectUsModal.getInstance().show(connectUsConfig);
                },
            };
            actions.push(action);
        }

        if (CommonTipsActionType.LOGIN & actionType) {
            const action = {
                text: actionText ?? i18n.t('登录'),
                onClick: () => {
                    this.logger?.warn(moduleName, 'click login');
                    CommonTips.getInstance().hide();
                    this.getExtensionCallBackAdapter()?.login?.();

                    if (callBack) {
                        callBack(CommonTipsActionType.LOGIN, errorCode);
                    }
                },
            };
            actions.push(action);
        }

        if (CommonTipsActionType.EXPORT & actionType) {
            const action = {
                text: actionText ?? i18n.t('备份到本地'),
                onClick: () => {
                    this.exportDoc();
                    callBack?.(CommonTipsActionType.EXPORT);
                    return false;
                },
            };
            actions.push(action);
        }

        if (CommonTipsActionType.EXPORT_SNAP_SHOT & actionType) {
            const action = {
                text: actionText ?? i18n.t('备份到本地'),
                onClick: () => {
                    this.exportSnapshot();
                    callBack?.(CommonTipsActionType.EXPORT_SNAP_SHOT);
                    return false;
                },
            };
            actions.push(action);
        }

        if (CommonTipsActionType.CREATE_COPY_DOC & actionType) {
            const action = {
                text: actionText ?? i18n.t('备份到在线文档'),
                onClick: () => {
                    callBack?.(CommonTipsActionType.CREATE_COPY_DOC, this.createCopyDoc);
                    return false;
                },
            };
            actions.push(action);
        }

        if (CommonTipsActionType.REPORT_WEBLOG & actionType) {
            const action = {
                text: actionText ?? i18n.t('上报日志'),
                onClick: () => {
                    window.log.loadAsset('WL_REPORT_DIALOG');
                    callBack?.(CommonTipsActionType.REPORT_WEBLOG);
                    return false;
                },
            };
            actions.push(action);
        }

        if (customerActions.length) {
            actions.push(...customerActions);
        }
        return actions;
    };

    private exportSnapshot = () => {
        SlideDataServices.EventManager()?.emit(EditEvents.ShowSnapShotModal);
        this.logger?.tdw?.({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PPT,
            action: TDWActionType.EXPORT_DATA_ERROR,
        });
    };

    private createCopyDoc = (isOffline: boolean) => {
        (this.appService.viewableServiceLayer as IPcEditorViewableServiceLayer)
            .produceBackupManager.promise()
            .then((produceBackupManager) => {
                produceBackupManager.createCopyDoc(isOffline);
            });
    };

    private exportDoc = () => {
        // eslint-disable-next-line no-underscore-dangle
        this.getMenuManager()?._actionNativePPT();
    };

    private openHistoryList = () => {
        const editableServiceLayer = this.appService.editableServiceLayer as IPcEditorAppServices['editableServiceLayer'];
        editableServiceLayer?.historyManager?.show();
    };

    private connectUs = () => {
        this.appService.viewableServiceLayer.accusationService.promise().then((accusationService) => {
            accusationService.showSupportPage(true);
        });
    };

    private exportDBToJson = async () => {
        const collab = await OnlineEditServices.CollaborationManager.promise();
        const data = await collab.getPersistent().exportDBData();
        const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'data.json';
        a.click();
        // 清理URL对象
        URL.revokeObjectURL(url);
    };

    private userReport = () => {
        this.logger?.getPrototype().report('USER_REPORT', {
            query: {
                startTs: Date.now() - (2 * 60 * 1000),
                endTs: Date.now(),
            },
        });
    };
}
